import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCatalog, priceOf, slugify } from '../CatalogContext';
import { useCart } from '../context/CartContext';
import { PageHero, SectionHead, ProductGrid } from '../components/Common';
import HeartButton from '../components/HeartButton';
import Reviews from '../components/Reviews';

export default function ProductDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loading, bySlug, byBrandSlug } = useCatalog();
  const { add } = useCart();
  const [active, setActive] = useState(0);
  const [variantIdx, setVariantIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  // Arriving via "Buy Now" on a card — bring the pack picker into view so the
  // shopper chooses a size before adding.
  useEffect(() => {
    if (params.get('buy') !== '1') return;
    const el = document.getElementById('pack-picker');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [params, slug]);

  if (loading) {
    return (
      <>
        <PageHero title="Product" />
        <section><div className="container"><p className="muted">Loading product…</p></div></section>
      </>
    );
  }

  const product = bySlug(slug);
  if (!product) return <Navigate to="/" replace />;

  const images = product.images?.length ? product.images : [];
  const variants = product.variants || [];
  const variant = variants[variantIdx] || null;

  // Headline price follows the selected pack size.
  const p = variant ? priceOf({ price: variant.price, mrp: variant.mrp }) : priceOf(product);

  const brandSlug = slugify(product.brand);
  const related = byBrandSlug(brandSlug).filter((r) => r.slug !== product.slug).slice(0, 4);

  const addToCart = () => {
    add(product, variant, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  const buyNow = () => {
    add(product, variant, qty);
    navigate('/cart');
  };

  return (
    <>
      <PageHero title={product.title} />

      <section>
        <div className="container profile-grid">
          {/* Gallery */}
          <div className="profile-image">
            {images.length ? <img src={images[active]} alt={product.title} /> : null}
            {images.length > 1 ? (
              <div className="thumb-row">
                {images.map((im, i) => (
                  <button
                    key={im}
                    className={`thumb ${i === active ? 'active' : ''}`}
                    onClick={() => setActive(i)}
                    aria-label={`Image ${i + 1}`}
                  >
                    <img src={im} alt="" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* Details */}
          <div className="profile-content">
            <span className="section-tag">{product.brand}</span>
            <h2>{product.title}</h2>

            {product.code ? <p className="product-sku">Pack Code: <strong>{product.code}</strong></p> : null}

            {p ? (
              <div className="price-row" style={{ justifyContent: 'flex-start', margin: '10px 0 20px' }}>
                <span className="price-now" style={{ fontSize: 32 }}>₹{p.price.toLocaleString('en-IN')}</span>
                {p.mrp ? <span className="price-mrp" style={{ fontSize: 18 }}>₹{p.mrp.toLocaleString('en-IN')}</span> : null}
                {p.off > 0 ? <span className="price-off" style={{ fontSize: 13 }}>{p.off}% OFF</span> : null}
              </div>
            ) : null}

            {product.shortDescription ? <p><strong>{product.shortDescription}</strong></p> : null}
            {product.description ? <p>{product.description}</p> : null}

            {/* Pack sizes — pick one, each with its own price + discount */}
            {variants.length ? (
              <>
                <h3 className="pick-title" id="pack-picker">Select Pack Size</h3>
                <div className="pack-grid">
                  {variants.map((v, i) => {
                    const vOff = v.mrp > v.price ? Math.round(((v.mrp - v.price) / v.mrp) * 100) : 0;
                    return (
                      <button
                        className={`pack-card ${i === variantIdx ? 'active' : ''}`}
                        key={v.weight}
                        onClick={() => setVariantIdx(i)}
                      >
                        <strong>{v.weight}</strong>
                        <span className="pack-price">₹{Number(v.price).toLocaleString('en-IN')}</span>
                        <span className="pack-sub">
                          {v.mrp > v.price ? <span className="price-mrp">₹{Number(v.mrp).toLocaleString('en-IN')}</span> : null}
                          {vOff > 0 ? <span className="price-off">{vOff}% OFF</span> : null}
                        </span>
                        {v.moq > 0 && v.moqPrice > 0 ? (
                          <span className="moq-hint">Buy {v.moq}+ at ₹{v.moqPrice}/unit</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : null}

            {product.features?.length ? (
              <ul className="tick-list" style={{ gridTemplateColumns: '1fr', margin: '18px 0' }}>
                {product.features.map((f) => (
                  <li key={f}><i className="fas fa-check" /> <span>{f}</span></li>
                ))}
              </ul>
            ) : null}

            {/* Quantity + cart */}
            <div className="buy-row">
              <div className="qty">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))}>−</button>
                <span>{qty}</span>
                <button onClick={() => setQty((q) => q + 1)}>+</button>
              </div>

              <button className={`consult-btn ${added ? 'added' : ''}`} onClick={addToCart}>
                {added ? <><i className="fas fa-check" /> Added to Cart</> : <><i className="fas fa-cart-plus" /> Add to Cart</>}
              </button>

              <button className="btn-gold" onClick={buyNow}>Buy Now</button>

              <HeartButton productId={product._id} className="lg" />
            </div>

            <Link className="link-btn" to="/contact-us">Enquire about bulk orders</Link>
          </div>
        </div>
      </section>

      {/* Ratings & reviews from verified buyers */}
      <Reviews productId={product._id} />

      {related.length ? (
        <section>
          <div className="container">
            <SectionHead title={`More From ${product.brand}`} />
            <ProductGrid products={related} />
          </div>
        </section>
      ) : null}
    </>
  );
}
